import React, { useRef, useState } from "react";
import { Button, CardList, Modal } from "@panda0/panda0-ui";
import {
  BarChart,
  BriefcaseBusiness,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Download,
  FileText,
  Flag,
  Linkedin,
  Minus,
  PanelTopCloseIcon,
  Plus,
  UserIcon,
  Video,
} from "lucide-react";

import SpeedometerChart from "../charts/speedometerchart";
import CandidateProfileSummary from "./candidate-profile-summary";
import Abilities from "./cognitive-abilities-section";
import CustomQuestions from "./custom-questions-section";
import GitHubStats from "./github-section";
import LinkedInSection from "./linkedin-section";
// import NotesSection from "./notes-section";
import ProctoringSection from "./proctoring-section";
import QuestionAnalysis from "./question-analysis-section";
import QuizResults from "./quiz-results-section";
import ScorecardSidebar from "./scorecard-sidebar";
// import SnapshotsSection from "./snapshots-section";
import TechnicalSkills from "./technical-skills";

const CandidateScorecard = ({
  candidate,
  isOpen,
  setOpen,
  onClose,
  candidates,
  shortlistedCandidates,
  toggleShortlist,
  downloadPDF,
  linkedInData,
  userData,
  heatMapValue,
  quizQuestions,
  scorecardRef,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isStarSelected, setIsStarSelected] = useState(false);

  // Refs for different sections
  const candidateRef = useRef(null);
  const videoResumeRef = useRef(null);
  const linkedInRef = useRef(null);
  const githubStatsRef = useRef(null);
  const questionAnalysisRef = useRef(null);
  const abilitiesRef = useRef(null);
  const questionRef = useRef(null);
  const skillRef = useRef(null);
  const proctoringRef = useRef(null);
  const snapshotsRef = useRef(null);
  // const notesRef = useRef(null);
  const pandaQuizRef = useRef(null);

  const steps = [
    { id: "overview", label: "Overview", icon: UserIcon, ref: candidateRef },
    // { id: "quiz", label: "Quiz Results", icon: FileText, ref: pandaQuizRef },
    {
      id: "skills",
      label: "Skill Analysis",
      icon: Cpu,
      ref: pandaQuizRef,
    },
    { id: "video", label: "Video Resume", icon: Video, ref: videoResumeRef },
    // { id: "abilities", label: "Abilities", icon: FileText, ref: abilitiesRef },
    {
      id: "linkedin",
      label: "Resume",
      icon: BriefcaseBusiness,
      ref: linkedInRef,
    },
    { id: "github", label: "GitHub", icon: BarChart, ref: githubStatsRef },
    // {
    //   id: "question",
    //   label: "Custom Questions",
    //   icon: CircleHelp,
    //   ref: questionRef,
    // },
    {
      id: "proctoring",
      label: "Proctoring",
      icon: Flag,
      ref: proctoringRef,
    },
    {
      id: "quiz-results",
      label: "Question Analysis",
      icon: FileText,
      ref: questionAnalysisRef,
    },
    // {
    //   id: "notes",
    //   label: "Notes",
    //   icon: FileText,
    //   ref: notesRef,
    // },
  ];

  const handleStepChange = (index) => {
    setActiveStep(index);
    //@ts-ignore
    steps[index].ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleStarClick = () => {
    setIsStarSelected(!isStarSelected);
  };

  // Navigate between steps
  const goToNextStep = () => {
    if (activeStep < steps.length - 1) {
      handleStepChange(activeStep + 1);
    }
  };

  const goToPrevStep = () => {
    if (activeStep > 0) {
      handleStepChange(activeStep - 1);
    }
  };

  return (
    <Modal
      showModal={isOpen}
      fullScreen={true}
      setShowModal={setOpen}
      onClose={onClose}
      className="max-w-none w-screen h-screen m-0 p-0"
    >
      <div
        className="flex flex-col h-screen w-screen overflow-hidden"
        ref={scorecardRef}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <div className="flex items-center">
            <span className="text-xl font-semibold mr-4">
              Candidate Scorecard
            </span>
            <div className="flex items-center bg-neutral-100 rounded-md px-3 py-1 text-sm">
              <span className="text-neutral-500 mr-2">ID:</span>
              <span className="font-medium">{candidate.candidateid}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              text={"Prev Candidate"}
              icon={<ChevronLeft className="h-4 w-4" />}
              onClick={() => {}}
              className="h-9"
            />
            <Button
              variant="primary"
              text={"Next Candidate"}
              icon={<ChevronRight className="h-4 w-4" />}
              onClick={() => {}}
              className="h-9"
            />
            <Button
              variant={
                shortlistedCandidates.includes(candidate.id)
                  ? "destructive"
                  : "primary"
              }
              text={
                shortlistedCandidates.includes(candidate.id)
                  ? "Remove from Shortlist"
                  : "Shortlist Candidate"
              }
              icon={
                shortlistedCandidates.includes(candidate.id) ? (
                  <Minus className="h-4 w-4" />
                ) : (
                  <Plus className="h-4 w-4" />
                )
              }
              onClick={() => toggleShortlist(candidate.id)}
              className="h-9"
            />
            <Button
              variant="primary"
              text="Download PDF"
              icon={<Download className="h-4 w-4" />}
              onClick={downloadPDF}
              className="h-9"
            />
            <Button
              variant="primary"
              icon={<PanelTopCloseIcon />}
              text="Close"
              onClick={() => setOpen(false)}
              className="h-9"
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Enhanced Stepper Sidebar */}
          {/* <ScorecardSidebar
            candidate={candidate}
            activeStep={activeStep}
            handleStepChange={handleStepChange}
            isStarSelected={isStarSelected}
            handleStarClick={handleStarClick}
            goToNextStep={goToNextStep}
            goToPrevStep={goToPrevStep}
            steps={steps}
          /> */}

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-row">
              {/* Content */}
              <div className="flex-1 p-6">
                {/* Candidate Overview */}
                <div ref={candidateRef} className="pb-10" id="overview">
                  <div className="flex items-center mb-4">
                    <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3">
                      <UserIcon className="h-4 w-4" />
                    </div>
                    <h2 className="text-xl font-bold">Candidate Overview</h2>
                  </div>

                  {/* Insert the profile summary component */}
                  <CandidateProfileSummary candidate={candidate} />
                  <div className="">
                    <div className="flex space-x-4">
                      <div className="w-1/2 p-4 rounded-lg flex flex-col">
                        <CardList.Card outerClassName="border rounded-lg shadow-sm p-4 flex-grow">
                          <h4 className="text-sm font-medium text-neutral-500 mb-3">
                            Candidate Information
                          </h4>
                          <div className="space-y-3">
                            <div className="flex items-center py-2 border-b border-neutral-100">
                              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 mr-3">
                                <svg
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="text-sm text-neutral-500">
                                  Email
                                </div>
                                <div className="font-medium">
                                  {candidate.name
                                    .toLowerCase()
                                    .replace(/\s+/g, ".")}
                                  @example.com
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center py-2 border-b border-neutral-100">
                              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-600 mr-3">
                                <svg
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="text-sm text-neutral-500">
                                  Phone
                                </div>
                                <div className="font-medium">
                                  +1 (555) 123-4567
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center py-2 border-b border-neutral-100">
                              <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-600 mr-3">
                                <svg
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="text-sm text-neutral-500">
                                  Location
                                </div>
                                <div className="font-medium">New York, NY</div>
                              </div>
                            </div>

                            <div className="flex items-center py-2 border-b border-neutral-100">
                              <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center text-yellow-600 mr-3">
                                <svg
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="text-sm text-neutral-500">
                                  Applied On
                                </div>
                                <div className="font-medium">
                                  {candidate.date}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center py-2">
                              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-600 mr-3">
                                <svg
                                  className="h-5 w-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <div className="text-sm text-neutral-500">
                                  Source
                                </div>
                                <div className="font-medium">
                                  {candidate.source}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center py-2">
                              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 mr-3">
                                <Linkedin className="h-5 w-5" />
                              </div>
                              <div>
                                <div className="text-sm text-neutral-500">
                                  LinkedIn
                                </div>
                                <div className="font-medium">
                                  <a
                                    href={`https://linkedin.com/in/${candidate.name
                                      .toLowerCase()
                                      .replace(/\s+/g, "-")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    linkedin.com/in/
                                    {candidate.name
                                      .toLowerCase()
                                      .replace(/\s+/g, "-")}
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardList.Card>
                      </div>
                      <div className="w-1/2 bg-white p-4 rounded-lg flex flex-col">
                        <CardList.Card outerClassName="flex-grow border rounded-lg shadow-sm p-4">
                          <CustomQuestions questionRef={questionRef} />
                        </CardList.Card>
                      </div>
                    </div>
                    <div className="flex space-x-4 ">
                      <div className="w-1/2 p-4 rounded-lg flex flex-col">
                        <CardList.Card outerClassName="border rounded-lg shadow-sm p-4">
                          <h4 className="text-sm font-medium text-neutral-500 mb-3">
                            Job Fit Score
                          </h4>

                          {/* Container for Indicator and Chart - Side by Side */}
                          <div className="flex items-center space-x-6">
                            {/* Circular Indicator */}
                            <div className="relative h-32 w-32 flex-shrink-0">
                              <div className="absolute inset-0 flex items-center justify-center flex-col">
                                <span className="text-3xl font-bold">
                                  {candidate.jobFit}%
                                </span>
                                <span className="text-sm text-neutral-500">
                                  Job Fit
                                </span>
                              </div>
                              <svg
                                className="h-full w-full"
                                viewBox="0 0 36 36"
                              >
                                <path
                                  d="M18 2.0845
                                 a 15.9155 15.9155 0 0 1 0 31.831
                                 a 15.9155 15.9155 0 0 1 0 -31.831"
                                  fill="none"
                                  stroke="#E6E6E6"
                                  strokeWidth="3"
                                />
                                <path
                                  d="M18 2.0845
                                   a 15.9155 15.9155 0 0 1 0 31.831
                                   a 15.9155 15.9155 0 0 1 0 -31.831"
                                  fill="none"
                                  stroke="#15a349"
                                  strokeWidth="3"
                                  strokeDasharray={`${parseInt(
                                    candidate.jobFit
                                  )}, 100`}
                                />
                              </svg>
                            </div>

                            {/* Skill Breakdown Chart */}
                            <div className="flex-grow">
                              <div className="space-y-3">
                                {/* Technical Skills */}
                                <div>
                                  <div className="flex justify-between mb-1">
                                    <span className="text-sm">
                                      Technical Skills
                                    </span>
                                    <span className="text-sm font-medium">
                                      85%
                                    </span>
                                  </div>
                                  <div className="h-2 w-full bg-neutral-100 rounded-full">
                                    <div
                                      className="h-full bg-green-500 rounded-full"
                                      style={{ width: "85%" }}
                                    ></div>
                                  </div>
                                </div>

                                {/* Experience Match */}
                                <div>
                                  <div className="flex justify-between mb-1">
                                    <span className="text-sm">
                                      Experience Match
                                    </span>
                                    <span className="text-sm font-medium">
                                      90%
                                    </span>
                                  </div>
                                  <div className="h-2 w-full bg-neutral-100 rounded-full">
                                    <div
                                      className="h-full bg-green-500 rounded-full"
                                      style={{ width: "90%" }}
                                    ></div>
                                  </div>
                                </div>

                                {/* Cognitive Skills */}
                                <div>
                                  <div className="flex justify-between mb-1">
                                    <span className="text-sm">
                                      Cognitive Skills
                                    </span>
                                    <span className="text-sm font-medium">
                                      80%
                                    </span>
                                  </div>
                                  <div className="h-2 w-full bg-neutral-100 rounded-full">
                                    <div
                                      className="h-full bg-green-500 rounded-full"
                                      style={{ width: "80%" }}
                                    ></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardList.Card>
                      </div>
                      <div className="w-1/2 p-4 rounded-lg flex flex-col">
                        <CardList.Card outerClassName="border rounded-lg shadow-sm p-4">
                          <h4 className="text-sm font-medium text-neutral-500 mb-3">
                            Experience
                          </h4>
                          <div className="flex flex-col md:flex-row items-center gap-10">
                            <div className="flex justify-center">
                              <div className="h-32 w-32">
                                <SpeedometerChart
                                  value={candidate.experience * 10}
                                  experience={candidate.experience.toString()}
                                />
                              </div>
                            </div>
                            <div className="max-w-xs">
                              <h4 className="text-2xl font-bold mb-2">
                                Professional Experience
                              </h4>
                              <p className="text-gray-600 mb-4">
                                5 years of hands-on experience in the field
                              </p>
                            </div>
                          </div>
                        </CardList.Card>
                      </div>
                    </div>
                  </div>
                </div>
                <QuizResults
                  pandaQuizRef={pandaQuizRef}
                  questions={quizQuestions.questions}
                />
                <TechnicalSkills skillRef={skillRef} candidates={candidates} />

                <Abilities
                  abilitiesRef={abilitiesRef}
                  questions={quizQuestions.questions}
                />

                {/* Video Resume Section */}
                <div
                  ref={videoResumeRef}
                  className="pb-10 pt-6 border-t"
                  id="video"
                >
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-3">
                      <Video className="h-4 w-4" />
                    </div>
                    <h2 className="text-xl font-bold">Video Resume</h2>
                  </div>

                  <div className="flex justify-center mt-6">
                    <CardList.Card outerClassName="w-full max-w-3xl border rounded-lg shadow-sm overflow-hidden">
                      <video controls className="w-full aspect-video">
                        <source src="your-video-url.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                      <div className="p-4 border-t">
                        <h3 className="font-medium">Video Introduction</h3>
                        <p className="text-sm text-neutral-600 mt-1">
                          In this 2-minute video, the candidate introduces their
                          background, skills, and explains why they&apos;re a
                          good fit for the position.
                        </p>
                        <div className="flex mt-3">
                          <span className="text-xs font-medium mr-2 px-2.5 py-0.5 rounded bg-blue-100 text-blue-800">
                            Length: 2:15
                          </span>
                          <span className="text-xs font-medium mr-2 px-2.5 py-0.5 rounded bg-purple-100 text-purple-800">
                            Recorded: {candidate.date}
                          </span>
                        </div>
                      </div>
                    </CardList.Card>
                  </div>
                </div>

                <LinkedInSection
                  linkedInRef={linkedInRef}
                  data={linkedInData}
                />

                <GitHubStats
                  githubStatsRef={githubStatsRef}
                  userData={userData}
                  value={heatMapValue}
                />

                {/* <CustomQuestions questionRef={questionRef} /> */}

                <ProctoringSection proctoringRef={proctoringRef} />

                {/* <SnapshotsSection snapshotsRef={snapshotsRef} /> */}
                <QuestionAnalysis
                  questionAnalysisRef={questionAnalysisRef}
                  questions={quizQuestions.questions}
                />

                {/* <NotesSection notesRef={notesRef} /> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(CandidateScorecard);
